import {
  createFilterWrapper,
  defaultWindow,
  throttleFilter,
  timestamp,
  useEventListener,
  UseIdleOptions,
  UseIdleReturn,
  WindowEventName
} from '@vueuse/core'
import { router } from '@/router'

import { useOrgInfoStore } from '@/stores/modules/orgInfo'
import { useCallCenter } from '@/stores/modules/callCenter'
import { callCenterStatusEnum, dialingScenes } from '@/enums/callCenter'
const defaultEvents: WindowEventName[] = [
  'mousemove',
  'mousedown',
  'resize',
  'keydown',
  'touchstart',
  'wheel'
]
const oneMinute = 15_000
let timer: any = null
const listeners: any = []
/**
 * 用于处理安全空闲逻辑
 *
 * @param timeout - 空闲超时时间（以毫秒为单位），默认为一分钟
 * @param options - 配置选项对象
 * @param options.initialState - 空闲状态的初始值，默认为 false
 * @param options.listenForVisibilityChange - 是否监听可见性变化事件，默认为 true
 * @param options.events - 需要监听的事件列表，默认为一些默认事件
 * @param options.window - 指定的 window 对象，默认为浏览器默认 window 对象
 * @param options.eventFilter - 用于过滤事件的函数，默认为 50ms 内仅触发一次的节流函数
 * @returns {Object} - 返回一个对象，包含当前是否空闲、最后活动时间、空闲时间以及重置空闲状态的方法
 */
interface UseCustomIdleReturn extends UseIdleReturn {
  idleTime: Ref<number>
}
export function destroyed() {
  listeners?.forEach((clearup: any) => clearup?.())
  listeners.length = 0
  timer && clearTimeout(timer)
  timer = null
}
export function useCustomIdle(
  timeout: number = oneMinute,
  options: UseIdleOptions = {}
): UseCustomIdleReturn {
  const {
    initialState = false,
    listenForVisibilityChange = true,
    events = defaultEvents,
    window = defaultWindow,
    eventFilter = throttleFilter(50)
  } = options
  const idle = ref(initialState)
  const lastActive = ref(timestamp())
  const idleTime = ref<number>(timeout)
  timer && clearTimeout(timer)
  timer = null
  const reset = () => {
    idle.value = false
    // @ts-ignore
    clearTimeout(timer)
    // @ts-ignore
    timer = setTimeout(() => (idle.value = true), idleTime.value)
  }

  const onEvent = createFilterWrapper(eventFilter, () => {
    lastActive.value = timestamp()
    reset()
  })

  if (window) {
    const document = window.document
    for (const event of events) {
      const clearup = useEventListener(window, event, onEvent, {
        passive: true
      })
      listeners.push(clearup)
    }

    if (listenForVisibilityChange) {
      const clearupVisib = useEventListener(document, 'visibilitychange', () => {
        if (!document.hidden) onEvent()
      })
      listeners.push(clearupVisib)
    }

    reset()
  }

  return {
    idle,
    lastActive,
    idleTime,
    reset
  }
} /**
 * 使用安全空闲逻辑的自定义 React Hooks
 *
 * @returns {Object} - 包含 dialogFormVisible（登录超时对话框的可见性）、reset（重置空闲计时器的方法）和 idleTime（空闲时间）的对象
 */
export function useSecurityIdle() {
  const { showLoginVisible, loginVisible } = useOrgInfoStore()
  // const dialogFormVisible = ref(false)
  const init = (time: number) => {
    const { idle } = useCustomIdle(time)
    // // 当路由名称发生变化时，更新 dialogFormVisible 的值

    // watch(() => route.name, (val) => {
    //   dialogFormVisible.value = val === 'Login' ? false : !!localStorage.getItem('loginTimout')
    //   console.log('%c [ dialogFormVisible.value ]-89', 'font-size:13px; background:#c3fd87; color:#ffffcb;', dialogFormVisible.value)
    // })
    // // 当 sysInfo.outLoginTimeDelay 的值发生变化时，更新 useCustomIdle 中的 outLoginTimeDelay 值并重置计时器

    // watch(() => sysInfo?.config?.outLoginTimeDelay, (newVal) => {
    //   idleTime.value = newVal * 60 * 1000
    //   reset()
    // })
    // 当 idle 的值发生变化时，决定是否显示登录超时对话框
    watch(idle, idleValue => {
      const callCenter = useCallCenter()

      const route = router.currentRoute.value
      // 是否正在通话中
      const isCalling =
        callCenter.dialingScenes === dialingScenes.turnCall ||
        callCenter.dialingScenes === dialingScenes.turnCallApp ||
        callCenter.callCenterStatus === callCenterStatusEnum.CALLING
      // 登录页、群呼通话中不弹
      if (idleValue && route.name !== 'Login' && !loginVisible && !isCalling) {
        showLoginVisible(true)
        localStorage.setItem('loginTimout', 'true')
      }
    })
  }

  return {
    init,
    destroyed
  }
}
